import { createContext, ReactNode, useCallback, useEffect, useReducer, useState } from 'react'

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

import { ActionMap, AuthState, AuthUser, CognitoContextType } from '../types/auth'

import { cognitoConfig } from '../config'
import { useNavigate } from 'react-router-dom'
import { getMe } from '../services/userService'
import { MFAType } from '../enums'
import { useLanguage } from './LanguageContext'
import Language from '../enums/Language'

const INITIALIZE = 'INITIALIZE'
const SIGN_OUT = 'SIGN_OUT'

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId || '',
  ClientId: cognitoConfig.clientId || ''
})

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  currentUser: null,
  setCurrentUser: () => false
}

type AuthActionTypes = {
  [INITIALIZE]: {
    isAuthenticated: boolean
    currentUser: AuthUser
  }
  [SIGN_OUT]: undefined
}

type CognitoActions = ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>]

const reducer = (state: AuthState, action: CognitoActions) => {
  if (action.type === INITIALIZE) {
    const { isAuthenticated, currentUser } = action.payload
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      currentUser
    }
  }

  if (action.type === SIGN_OUT) {
    return {
      ...state,
      isAuthenticated: false,
      currentUser: null
    }
  }
  return state
}

export const AuthContext = createContext<CognitoContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  /**
   * The navigate function
   */
  const navigate = useNavigate()

  const [state, dispatch] = useReducer(reducer, initialState)

  /**
   * Is TOTP required
   */
  const [totpRequired, setTotpRequired] = useState<boolean>(false)

  /**
   * Is new password required
   */
  const [newPasswordRequired, setNewPasswordRequired] = useState<boolean>(false)

  /**
   * Is email verification required
   */
  const [emailVerificationRequired, setEmailVerificationRequired] = useState<boolean>(false)

  /**
   * Authenticated user session
   */
  const [authSession, setAuthSession] = useState<CognitoUser | undefined>(undefined)

  /**
   * The preferred MFA settings.
   */
  const [preferredMfaSetting, setPreferredMfaSetting] = useState<string | undefined>()

  /**
   * Set the current user.
   */
  const setCurrentUser = (user: AuthUser) => {
    dispatch({
      type: INITIALIZE,
      payload: { isAuthenticated: true, currentUser: user }
    })
  }

  /**
   * Get user attributes
   */
  const getUserAttributes = useCallback(
    (currentUser: CognitoUser): Promise<Record<string, string>> =>
      new Promise((resolve, reject) => {
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err)
          } else {
            const results: Record<string, string> = {}

            attributes?.forEach((attribute) => {
              results[attribute.Name] = attribute.Value
            })
            resolve(results)
          }
        })
      }),
    []
  )

  const { setLanguage } = useLanguage()

  const _changeLanguage = (language: Language) => {
    setLanguage(language)
  }

  const getSession = useCallback(
    () =>
      new Promise((resolve, reject) => {
        // The cognito user.
        const cognitoUser = userPool.getCurrentUser()

        if (cognitoUser) {
          cognitoUser.getSession(
            async (error: Error | null, session: CognitoUserSession | null) => {
              if (error) {
                reject(error)
              } else {
                try {
                  // Get authenticated user.
                  const user = await getMe()

                  // Set the language.
                  // changeLanguage(user.language)

                  dispatch({
                    type: INITIALIZE,
                    payload: { isAuthenticated: true, currentUser: user }
                  })

                  resolve({
                    user,
                    session
                  })
                } catch (error) {
                  reject(error)
                }
              }
            }
          )
        } else {
          dispatch({
            type: INITIALIZE,
            payload: {
              isAuthenticated: false,
              currentUser: null
            }
          })
        }
      }),
    [getUserAttributes]
  )

  const initialize = useCallback(async () => {
    try {
      await getSession()
    } catch {
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          currentUser: null
        }
      })
    }
  }, [getSession])

  useEffect(() => {
    initialize()
  }, [initialize])

  /**
   * Get the ID token.
   */
  const getIdToken = () => {
    return userPool.getCurrentUser()?.getSignInUserSession()?.getIdToken()
  }

  /**
   * Get the access token.
   */
  const getAccessToken = () => {
    return userPool.getCurrentUser()?.getSignInUserSession()?.getAccessToken()
  }

  /**
   * Get the refresh token.
   */
  const getRefreshToken = () => {
    return userPool.getCurrentUser()?.getSignInUserSession()?.getRefreshToken()
  }

  /**
   * Sign in.
   */
  const signIn = useCallback(
    (email: string, password: string, newPassword?: string) =>
      new Promise((resolve, reject) => {
        const user = new CognitoUser({
          Username: email,
          Pool: userPool
        })

        const authDetails = new AuthenticationDetails({
          Username: email,
          Password: password
        })

        user.authenticateUser(authDetails, {
          onSuccess: async (_data: CognitoUserSession) => {
            // Is user soft deleted by company admin
            const isSpinedVerified = await isUserVerified()
            if (!isSpinedVerified) {
              //TODO: Change error message to more fitting if needed
              reject(new Error('Something went wrong!'))
            } else {
              user.getUserAttributes(async function (err, attributes) {
                if (err) {
                  reject(err)
                } else {
                  // TODO: Check if phone is verifed.

                  const emailVerified =
                    attributes !== undefined ? await isEmailVerified(attributes) : false

                  if (emailVerified) {
                    await getSession()
                    resolve(attributes)
                  } else {
                    await sendAttributeVerificationCode(email, password)
                    setEmailVerificationRequired(true)
                  }
                }
              })
            }
          },
          onFailure: (err) => {
            reject(err)
          },
          newPasswordRequired: () => {
            if (newPassword) {
              user.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onFailure: (err) => {
                    reject(err)
                  },
                  onSuccess: async (result) => {
                    await getSession()
                    setNewPasswordRequired(false)
                    resolve(result)
                  }
                }
              )
            } else setNewPasswordRequired(true)
          },
          totpRequired: (challengeName) => {
            // window.localStorage.setItem('preferredMfaSetting', challengeName)
            setPreferredMfaSetting(challengeName)
            setAuthSession(user)
            return setTotpRequired(true)
          },
          mfaRequired: (challengeName) => {
            // window.localStorage.setItem('preferredMfaSetting', challengeName)
            setPreferredMfaSetting(challengeName)
            setAuthSession(user)
            return setTotpRequired(true)
          }
        })
      }),
    [getSession]
  )

  /**
   * Handle MFA sign in code.
   *
   * @param mfaCode
   */
  const handleMfaSignInCode = async (mfaCode: string) => {
    await new Promise((resolve, reject) =>
      authSession?.sendMFACode(
        mfaCode,
        {
          onSuccess: async (data) => {
            await getSession()
            setTotpRequired(false)
            resolve(data)
          },
          onFailure: (err) => {
            reject(err)
          }
        },
        preferredMfaSetting
      )
    )
  }

  const completeSignIn = async () => {
    return
  }

  /**
   * Sign out.
   */
  const signOut = () => {
    // Get the Cognito user.
    const cognitoUser = userPool.getCurrentUser()

    // Reset TOTP state to false.
    setTotpRequired(false)

    // Reset new password required state to false
    setNewPasswordRequired(false)

    // Reset preferred MFA type.
    setPreferredMfaSetting(undefined)

    // Reset email verification required state to false.
    setEmailVerificationRequired(false)

    // Reset session.
    setAuthSession(undefined)

    // Remove MFA type from locaStorage.
    window.localStorage.removeItem('mfaType')

    // Sign the cognito user out.
    cognitoUser?.signOut()

    dispatch({ type: SIGN_OUT })
  }

  /**
   * Sign up.
   */
  const signUp = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
  ) =>
    new Promise((resolve, reject) => {
      userPool.signUp(
        email,
        password,
        [
          new CognitoUserAttribute({ Name: 'email', Value: email }),
          new CognitoUserAttribute({
            Name: 'name',
            Value: email
          }),
          new CognitoUserAttribute({
            Name: 'family_name',
            Value: lastName
          }),
          new CognitoUserAttribute({
            Name: 'given_name',
            Value: firstName
          }),
          new CognitoUserAttribute({
            Name: 'phone_number',
            Value: phoneNumber
          })
        ],
        [],
        async (error) => {
          if (error) {
            reject(error)
          } else {
            resolve(undefined)
          }
        }
      )
    })

  /**
   * Send reset password verification code to email.
   *
   * @param email
   */
  const resetPassword = (email: string) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      })

      cognitoUser.forgotPassword({
        onSuccess: (data) => {
          resolve(data)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  /**
   * Confirm new password.
   *
   * @param email
   * @param verificationCode
   * @param newPassword
   */
  const confirmNewPassword = (email: string, verificationCode: string, newPassword: string) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      })

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve(navigate('/auth/sign-in'))
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  /**
   * Change the password.
   *
   * @param oldPassword
   * @param newPassword
   */
  const changePassword = (oldPassword: string, newPassword: string) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser()

      const authDetails = new AuthenticationDetails({
        Username: cognitoUser?.getUsername() as string,
        Password: oldPassword
      })

      cognitoUser?.authenticateUser(authDetails, {
        onSuccess: () => {
          cognitoUser?.changePassword(oldPassword, newPassword, (err, _success) => {
            if (err) {
              reject(err)
            } else {
              resolve(undefined)
            }
          })
        },
        onFailure: (error) => {
          reject(error)
        }
      })
    })
  }

  /**
   * Confirm the registration.
   *
   * @param email
   * @param verificationCode
   */
  const confirmRegistration = (email: string, verificationCode: string) => {
    return new Promise<void>((resolve, reject) => {
      const userData = {
        Username: email,
        Pool: userPool
      }
      const cognitoUser = new CognitoUser(userData)

      cognitoUser.confirmRegistration(verificationCode, true, (err, _result) => {
        if (err) {
          reject(err)
        } else {
          resolve(navigate('/auth/sign-in'))
        }
      })
    })
  }

  /**
   * Delete the user.
   */
  const deleteCognitoUser = async () => {
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirms that the user is authenticated.
    await new Promise((res) => cognitoUser?.getSession(res))

    await new Promise((resolve, reject) => {
      cognitoUser?.deleteUser(function (err, _result) {
        if (err) {
          reject(err)
        } else {
          resolve(navigate('/auth/sign-in'))
        }
      })
    })
  }

  /**
   * Update the cognito user attributes.
   *
   * @param firstName
   * @param lastName
   * @param phoneNumber
   */
  const updateUserAttributes = async (firstName: string, lastName: string, phoneNumber: string) => {
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirm that the user is authenticated and
    // authorized to update attributes.
    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) => {
      cognitoUser?.updateAttributes(
        [
          new CognitoUserAttribute({
            Name: 'family_name',
            Value: lastName
          }),
          new CognitoUserAttribute({
            Name: 'given_name',
            Value: firstName
          }),
          new CognitoUserAttribute({
            Name: 'phone_number',
            Value: phoneNumber
          })
        ],
        (err, result) => {
          if (err) {
            reject(err)
          } else {
            resolve(result)
          }
        }
      )
    })
  }

  /**
   * Handle TOTP (Time-based One-Time Password) submission.
   *
   * @param totp The TOTP code.
   */
  const onTotpFormSubmit = async (totp: string) => {
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirm that the user is authenticated and
    // authorized.
    await new Promise((res) => cognitoUser?.getSession(res))

    return await new Promise((resolve, reject) =>
      cognitoUser?.verifySoftwareToken(totp, '', {
        onSuccess: (result) => {
          resolve(result)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    )
  }

  /**
   * Enable or disable mfa for user
   * //TODO: should we allow users to use both or just one?
   * @param mfaType
   * @param enabled
   */

  const enableOrDisableMfa = async (mfaType: MFAType, enabled: boolean) => {
    // The software token MFA settings.
    const softwareTokenMfaSettings =
      mfaType === MFAType.AUTHENTICATOR
        ? {
            PreferredMfa: enabled,
            Enabled: enabled
          }
        : {
            PreferredMfa: false,
            Enabled: false
          }

    // The SMS MFA settings.
    const smsMfaSettings =
      mfaType === MFAType.SMS
        ? {
            PreferredMfa: enabled,
            Enabled: enabled
          }
        : {
            PreferredMfa: false,
            Enabled: false
          }

    // The cognito user.
    const cognitoUser = userPool.getCurrentUser()

    // Check user session to confirm that the user is authenticated and authorized.
    await new Promise((res) => cognitoUser?.getSession(res))

    await new Promise((resolve, reject) =>
      cognitoUser?.setUserMfaPreference(
        smsMfaSettings,
        softwareTokenMfaSettings,
        async function (err, result) {
          if (err) {
            reject(err)
          } else if (enabled) {
            switch (mfaType) {
              case MFAType.SMS:
                setPreferredMfaSetting('SMS_MFA')
                break
              case MFAType.AUTHENTICATOR:
                setPreferredMfaSetting('SOFTWARE_TOKEN_MFA')
                break
            }

            resolve(result)
          } else {
            setPreferredMfaSetting(undefined)
            resolve(result)
          }
        }
      )
    )
  }

  /**
   * Send attribute verification code to email.
   *
   * @param email
   * @param password
   */
  const sendAttributeVerificationCode = async (email: string, password: string) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    })

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    })

    await new Promise((resolve, reject) =>
      cognitoUser?.authenticateUser(authDetails, {
        onSuccess: () => {
          cognitoUser.getAttributeVerificationCode('email', {
            onSuccess: function (result) {
              resolve(result)
            },
            onFailure: function (err) {
              reject(err)
            }
          })
        },
        onFailure: (error) => {
          reject(error)
        }
      })
    )
  }

  const verifyAttribute = async (_email: string, verificationCode: string) => {
    // The cognito user.
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirm that the user is
    // authenticated and authorized.
    await new Promise((res) => cognitoUser?.getSession(res))

    await new Promise((resolve, reject) =>
      cognitoUser?.verifyAttribute('email', verificationCode, {
        onSuccess: async () => {
          setEmailVerificationRequired(false)
          resolve(await getSession())
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    )
  }

  /**
   * Check if the user has verified email address.
   */
  const isEmailVerified = async (attributes: CognitoUserAttribute[]) => {
    return attributes.some(
      (attribute) => attribute.Name === 'email_verified' && attribute.Value === 'true'
    )
  }

  const isUserVerified = async (): Promise<boolean> => {
    const cognitoUser = userPool.getCurrentUser()

    return new Promise((resolve) => {
      cognitoUser?.getSession(async (error: Error | null, session: CognitoUserSession | null) => {
        if (error !== null) {
          return resolve(false)
        } else {
          const payload = session?.getAccessToken().decodePayload()

          if (payload && 'spine-verified' in payload) {
            return resolve(true)
          } else {
            // We have to sign user out since cognitoUser.getSession function sets token to localStorage automatically.
            signOut()
            return resolve(false)
          }
        }
      })
    })
  }

  const sendPhoneNumberVerificationCode = async (): Promise<void> => {
    // The cognito user.
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirm that the user is
    // authenticated and authorized.
    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) => {
      cognitoUser?.getAttributeVerificationCode('phone_number', {
        onSuccess: (_success) => {
          resolve()
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  const sendEmailVerificationCode = async (): Promise<void> => {
    // The cognito user.
    const cognitoUser = userPool.getCurrentUser()

    // Check the user session to confirm that the user is
    // authenticated and authorized.
    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) => {
      cognitoUser?.getAttributeVerificationCode('email', {
        onSuccess: (_success) => {
          resolve()
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    })
  }

  const verifyEmail = async (verificationCode: string): Promise<void> => {
    const cognitoUser = userPool.getCurrentUser()

    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) =>
      cognitoUser?.verifyAttribute('email', verificationCode, {
        onSuccess: () => {
          resolve()
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    )
  }

  const verifyPhoneNumber = async (verificationCode: string): Promise<void> => {
    const cognitoUser = userPool.getCurrentUser()

    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) =>
      cognitoUser?.verifyAttribute('phone_number', verificationCode, {
        onSuccess: () => {
          resolve()
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    )
  }

  const associateSoftwareToken = async (): Promise<string> => {
    const cognitoUser = userPool.getCurrentUser()

    await new Promise((resolve) => cognitoUser?.getSession(resolve))

    return new Promise((resolve, reject) =>
      cognitoUser?.associateSoftwareToken({
        associateSecretCode: (secretCode) => {
          resolve(secretCode)
        },
        onFailure: (err) => {
          reject(err)
        }
      })
    )
  }

  const refreshToken = async (): Promise<void> => {
    const cognitoUser = userPool.getCurrentUser()
    return new Promise((resolve, reject) =>
      cognitoUser?.getSession(async (error: Error | null, session: CognitoUserSession | null) => {
        if (session) {
          cognitoUser.refreshSession(session.getRefreshToken(), getSession)

          resolve()
        } else {
          reject(error)
        }
      })
    )
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        setCurrentUser,
        method: 'cognito',
        totpRequired,
        newPasswordRequired,
        emailVerificationRequired,
        preferredMfaSetting,
        getIdToken,
        getAccessToken,
        getRefreshToken,
        signIn,
        signUp,
        signOut,
        resetPassword,
        confirmRegistration,
        changePassword,
        confirmNewPassword,
        deleteCognitoUser,
        updateUserAttributes,
        onTotpFormSubmit,
        enableOrDisableMfa,
        verifyAttribute,
        handleMfaSignInCode,
        completeSignIn,
        sendEmailVerificationCode,
        verifyEmail,
        sendPhoneNumberVerificationCode,
        verifyPhoneNumber,
        associateSoftwareToken,
        refreshToken
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
